import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import "./index.scss"

import { ConfigContainer } from "components"
import ToggleButton from "react-bootstrap/ToggleButton"
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup"
import { Duration } from "luxon"
import { Title, Container, Text, DurationInput, Select } from "components/Form"
import { members, message, analyses, seatInfo, customerAdministration, auth, limsCapabilities } from "state_management"
import ESignatureModal from "components/Lims/ESignatureModal"
import useT from "utils/hooks/useT"

const PLANNERTECH_LANGUAGE_CODES = ["en", "da", "fr", "it", "es", "pl", "de", "el", "pt", "cs", "ko"]
const PLANNERTECH_LANGUAGE_OPTIONS = [
  { id: "cs", name: "Čeština" },
  { id: "da", name: "Dansk" },
  { id: "de", name: "Deutsch" },
  { id: "el", name: "Ελληνικά" },
  { id: "en", name: "English" },
  { id: "es", name: "Español" },
  { id: "fr", name: "Français" },
  { id: "it", name: "Italiano" },
  { id: "ko", name: "한국어" },
  { id: "pl", name: "Polski" },
  { id: "pt", name: "Português" },
]

// Member config.
// Used both by ADMINs, MEMBERs and PLANNERs, so make sure it keeps working
// regardless of which membertype the currently logged in user is!
export default function MemberConfig({ member = null, adminTeamId = null, onClose }) {
  const authState = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const { t } = useT("common")

  const [role, setRole] = useState(member ? member.role : "MEMBER")
  const [startTime, setStartTime] = useState(
    member ? Duration.fromObject({ seconds: member.workHours.min }) : Duration.fromObject({ hours: 8 })
  )
  const [endTime, setEndTime] = useState(
    member ? Duration.fromObject({ seconds: member.workHours.max }) : Duration.fromObject({ hours: 16 })
  )
  const isCustomerAdmin = useSelector((state) => state.auth.role) === "customer_admin"
  const onlyLibrarian = useSelector((state) => state.teamStatus.onlyLibrarian)

  const [email, setEmail] = useState(member ? member.email : "")
  const [password, setPassword] = useState(null)
  const [initials, setInitials] = useState(member ? member.initials : "")
  const [showOnPlan, setShowOnPlan] = useState(member ? member.showOnPlan : true)
  const [outlookLink, setOutlookLink] = useState(member ? member.outlookLink : null)
  const [plannertechLanguage, setPlannertechLanguage] = useState(() => {
    if (!member) {
      return ""
    }
    return PLANNERTECH_LANGUAGE_CODES.includes(member.plannertechLanguage) ? member.plannertechLanguage : "en"
  })

  // LIMS QC capabilities (Analyst/Reviewer/QA/QA Manager) — shown only when the LIMS module is
  // on, for an EXISTING member (capabilities are granted via the e-signed
  // POST /lims/qc-capabilities after creation — never on the create payload). SPEC-021 (B3):
  // the current capabilities are read off the member dump (member.qcCapabilities). SPEC-007 D2
  // rev.: editing is allowed for a PLANNER (team lead), or a holder of the qa-manager
  // capability (delegated administration).
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const canAdministerCapabilities = useSelector(limsCapabilities.selectCanAdministerCapabilities)
  const [qcCapabilities, setQcCapabilities] = useState(() => (member && member.qcCapabilities) || [])
  const [initialCapabilities, setInitialCapabilities] = useState(() => (member && member.qcCapabilities) || [])
  const [esigOpen, setEsigOpen] = useState(false)
  const [pendingRes, setPendingRes] = useState(null)
  // A change to QC capabilities is an authorisation change → requires an e-signature.
  const capabilitiesChanged = () =>
    [...qcCapabilities].sort().join(",") !== [...initialCapabilities].sort().join(",")
  // Ordered to mirror the QC pipeline (SPEC-032 / F0); `supervisor` is the chemist lab-review role.
  const qcCapabilityOptions = [
    { id: "analyst", name: t("limsCapAnalyst") },
    { id: "reviewer", name: t("limsCapReviewer") },
    { id: "supervisor", name: t("limsCapSupervisor") },
    { id: "qa", name: t("limsCapQa") },
    { id: "qa-manager", name: t("limsCapQaManager") },
  ]
  const showCapabilitySection = showLims && !!member && canAdministerCapabilities

  // Performs the actual member edit-save. Called directly when no e-signature is needed,
  // or from the e-signature modal's onSign once a QC-role change has been signed — so
  // nothing is committed (and no success shown) until the signature is completed.
  // `capabilitySignature` is the full ESignatureModal payload: setForMember lifts its
  // password onto the signed POST /lims/qc-capabilities (verified + discarded server-side).
  const runEditSave = (res, capabilitySignature) => {
    const wasShownOnPlan = member.showOnPlan
    const credentialsPromise = password
      ? dispatch(members.updateCredentials(member.id, { password }))
      : Promise.resolve()
    return credentialsPromise
      .then(() => dispatch(members.edit(member.id, res)))
      .then(() => {
        if (wasShownOnPlan && !res.showOnPlan) {
          dispatch(analyses.fetchPlanned())
          dispatch(analyses.fetchUnplanned())
        }
        if (member.id === authState.memberId && res.plannertechLanguage) {
          dispatch(auth.setPlannertechLanguage(res.plannertechLanguage))
        }
      })
      .then(() => {
        if (showLims && capabilitiesChanged()) {
          return dispatch(limsCapabilities.setForMember(member.id, qcCapabilities, capabilitySignature))
        }
      })
  }


  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

  const renderDays = (disabled) => {
    return days.map((day) => {
      return (
        <ToggleButton
          disabled={disabled}
          className="Memberconfig-checkboxButton"
          value={day}
          title={day}
          variant="light"
          key={day}
          id={"member-config-"+day}
        >
          {day.substring(0, 3)}
        </ToggleButton>
      )
    })
  }

  const [workWeek, setWorkWeek] = useState(
    (() => {
      if (member) {
        let arr = []
        Object.entries(member.workWeek).map((entry) => {
          if (entry[1]) {
            arr.push(entry[0])
          }
          return null
        })
        return arr
      } else {
        return [days[0], days[1], days[2], days[3], days[4]]
      }
    })()
  )

  const checkOutlookLink = (link) => {
    let correctLink = true
    if(link && !link.includes('.ics')){
      dispatch(message.warning(t("mustBeIcsLink")))
      correctLink = false
    }
    if(link && link.length === 0){
      correctLink = true
    }
    setOutlookLink(link)
    return correctLink
  }

  const isPasswordValid = (password) => {
    // Define all password requirements and their respective regex patterns
    const requirements = [
      { name: t("atLeast8Characters"), pattern: /^.{8,}$/ },
      { name: t("atLeast1LowercaseLetter"), pattern: /[a-z]/ },
      { name: t("atLeast1UppercaseLetter"), pattern: /[A-Z]/ },
      { name: t("atLeast1Number"), pattern: /\d/ },
    ]

    if (member && !password) {
      return { valid: true, missingRequirements: [] }
    }

    let missingRequirements = []

    // Check each requirement
    for (let requirement of requirements) {
      if (!requirement.pattern.test(password)) {
        missingRequirements.push(requirement.name)
      }
    }

    // Return validity and list of missing requirements
    return {
      valid: missingRequirements.length === 0,
      missingRequirements: missingRequirements,
    }
  }

  const disabled = member && ((authState.isMember && member && authState.memberId !== member.id) || member.archived)
  return (
    <>
      {esigOpen && member && (
        <ESignatureModal
          title={t("limsEsigTitle")}
          onSign={(sig) =>
            // Return the promise: the signature box stays open with an inline error on a
            // backend 403 (wrong password / not authorised to administer capabilities) and
            // closes only on success. Refresh the baseline so the form is clean again.
            runEditSave(pendingRes, sig).then(() => {
              setEsigOpen(false)
              setInitialCapabilities(qcCapabilities)
            })
          }
          onCancel={() => setEsigOpen(false)}
        />
      )}
    <ConfigContainer
      disabled={disabled}
      title={member ? t("editMember") : t("addMember")}
      submitHandler={() => {
        const passwordResult = isPasswordValid(password)
        if(!passwordResult.valid) {
          const passwordMessage = t("missingPasswordRequirements", { requirements: passwordResult.missingRequirements.join(', ') })
          return dispatch(message.warning(passwordMessage))
        }
        const normalizedPlannertechLanguage = PLANNERTECH_LANGUAGE_CODES.includes(plannertechLanguage)
          ? plannertechLanguage
          : ""
        const currentPlannertechLanguage = member
          ? (PLANNERTECH_LANGUAGE_CODES.includes(member.plannertechLanguage) ? member.plannertechLanguage : "en")
          : ""
        const res = {
          initials,
          workHours: {
            min: startTime.as("seconds"),
            max: endTime.as("seconds"),
          },
          workWeek: (() => {
            let obj = {}
            days.map((day) => {
              obj[day] = false
              return null
            })
            workWeek.map((day) => {
              obj[day] = true
              return null
            })
            return obj
          })(),
          role,
          showOnPlan: role === "VIEW_ONLY" ? false : showOnPlan,
          outlookLink: (showOnPlan && role !== "VIEW_ONLY") ? (outlookLink || null) : null,
        }
        if (!member) {
          // Email and password are only sent on creation
          res.email = email
          res.password = password || undefined
        }
        if (!member && normalizedPlannertechLanguage) {
          res.plannertechLanguage = normalizedPlannertechLanguage
        }
        if (member && normalizedPlannertechLanguage && normalizedPlannertechLanguage !== currentPlannertechLanguage) {
          res.plannertechLanguage = normalizedPlannertechLanguage
        }
        // Only use this if the user is exists
        if (!checkOutlookLink(outlookLink)){
          dispatch(message.warning(t("invalidOutlookLink")))
        }
        else if (member) {
          // A QC-role change is an authorisation change: require the e-signature FIRST and
          // only commit (and show success) once it is signed. Otherwise save directly.
          if (showLims && capabilitiesChanged()) {
            setPendingRes(res)
            setEsigOpen(true)
          } else {
            runEditSave(res, null).then(onClose)
          }
        } else {
          // SPEC-021 (B3): a NEW member is created WITHOUT QC capabilities — granting them is an
          // e-signed qa-manager act performed afterwards via Edit member (the capability section
          // is hidden on create).
          if (adminTeamId) {
            dispatch(members.createAsAdmin({ ...res, teamId: adminTeamId })).then(() => {
              // Fetching customers in different ways, depending on if user is customer-admin or admin for a company
              if(isCustomerAdmin) {
                dispatch(customerAdministration.fetchCustomers())
              } else {
                dispatch(seatInfo.fetch())
              }
            }).then(onClose)
          } else {
            dispatch(members.create(res)).then(onClose)
          }
        }
      }}
      cancelHandler={onClose}
    >
      <Container>
        <Title>{t("initials")}</Title>
        <Text required disabled={disabled} value={initials} textTransform={"uppercase"} onChange={(value) => setInitials(value)} />
      </Container>
      <Container>
        <Title>{t("emailLabel")}</Title>
        <Text
          required
          type="email"
          disabled={disabled || !!member}
          value={email}
          onValidate={(value) => {
            if (value.includes("..")) {
              return t("emailCannotContainDoubleDots")
            }
          }}
          textTransform={"lowercase"}
          onChange={(value) => setEmail(value)}
        />
      </Container>
      <Container>
        <Title>{t("password")}</Title>
        <Text type="password" disabled={disabled} required={!member} value={password} onChange={setPassword} />
      </Container>
      <Container>
        <Title>{t("plannertechLanguage")}</Title>
        <Select
          isMulti={false}
          required={!!member}
          disabled={disabled}
          collection={PLANNERTECH_LANGUAGE_OPTIONS}
          value={plannertechLanguage}
          onChange={(values) => setPlannertechLanguage(values[0] || "")}
          includeNone={!member}
          noneLabel={t("default")}
        />
      </Container>
      <Container>
        <Title>{t("role")}</Title>
        <ToggleButtonGroup
          type="radio"
          className="Memberconfig-toggleButtonGroup"
          name="role"
          defaultValue={member ? member.role : "MEMBER"}
          onChange={setRole}
        >
          <ToggleButton
            disabled={disabled || authState.isMember || authState.isViewOnly}
            value="MEMBER"
            variant="light"
            id="memberconfig-ismember_id"
            className="Memberconfig-radioButton"
            >
            {t("member")}
          </ToggleButton>
          <ToggleButton
            disabled={disabled || authState.isMember || authState.isViewOnly}
            value="PLANNER"
            variant="light"
            id="memberconfig-isplanner_id"
            className="Memberconfig-radioButton"
          >
            {t("planner")}
          </ToggleButton>
          <ToggleButton
            disabled={disabled || authState.isMember || authState.isViewOnly}
            value="VIEW_ONLY"
            variant="light"
            id="memberconfig-isviewonly_id"
            className="Memberconfig-radioButton"
          >
            {t("viewOnly")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Container>
      {showCapabilitySection && (
        <Container>
          <Title>{t("limsQcCapabilities")}</Title>
          <Select
            collection={qcCapabilityOptions}
            value={qcCapabilities}
            onChange={setQcCapabilities}
            disabled={disabled || authState.isMember || authState.isViewOnly}
            menuPlacement="bottom"
          />
        </Container>
      )}
      {!onlyLibrarian && (
      <div>
      <Container>
        <Title>{t("showOnPlan")}</Title>
        <ToggleButtonGroup
          name="showOnPlan"
          className="Memberconfig-toggleButtonGroup"
          type="radio"
          value={role === "VIEW_ONLY" ? false : showOnPlan}
          onChange={(value) => {
            if (!value) setOutlookLink(null)
            setShowOnPlan(value)
          }}
        >
          <ToggleButton disabled={disabled || role === "VIEW_ONLY"} value={true} variant="light" id="memberconfig-showonplan_id" className="Memberconfig-radioButton">
            {t("shown")}
          </ToggleButton>
          <ToggleButton disabled={disabled || role === "VIEW_ONLY"} value={false} variant="light" id="memberconfig-hideonplan_id" className="Memberconfig-radioButton">
            {t("hidden")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Container>
      <Container>
        <Title>{t("outlookLinkLabel")}</Title>
        <Text type="url" disabled={disabled || !showOnPlan || role === "VIEW_ONLY"} value={outlookLink} onChange={checkOutlookLink} />
      </Container>
      <div className="MemberConfig-availability">
        <p>{t("availability")}</p>
        <label>{t("workHours")}</label>
        <Container>
          <Title>{t("from")}</Title>
          <DurationInput disabled={disabled || !showOnPlan || role === "VIEW_ONLY"} value={startTime} onChange={setStartTime} required isTime />
          <Title>{t("to")}</Title>
          <DurationInput disabled={disabled || !showOnPlan || role === "VIEW_ONLY"} value={endTime} onChange={setEndTime} required isTime />
        </Container>
        <label>{t("workWeek")}</label>
        <div className="MemberConfig-work-week">
          <Container>
            <ToggleButtonGroup
              variant="light"
              type="checkbox"
              value={workWeek}
              onChange={(e) => {
                setWorkWeek(e)
              }}
            >
              {renderDays(disabled || !showOnPlan || role === "VIEW_ONLY")}
            </ToggleButtonGroup>
          </Container>
        </div>
      </div>
      </div>
      )}
    </ConfigContainer>
    </>
  )
}
